import React from "react";
import { Link } from "react-router-dom";

import { Jumbotron } from "./migration";

const offerings = [
  {
    title: "Sales to Delivery Handoffs",
    points: [
      "Lead capture, qualification, assignment, and follow-up flows",
      "Deal-won triggers that create projects, tasks, folders, and alerts",
      "Reduced dependency on staff remembering the next operational step",
    ],
  },
  {
    title: "Operations and Reporting Work",
    points: [
      "Cross-system data movement where teams currently copy, paste, and check",
      "Reliable KPI and status reporting pulled from live operational sources",
      "Alerts and exception handling when a workflow stalls or data breaks",
    ],
  },
  {
    title: "Real-World Intake and Process Automation",
    points: [
      "Digitising awkward intake workflows, including scanned or handwritten inputs",
      "Tracking and matching workflows where traceability matters",
      "Custom solutions for edge cases that standard integrations do not handle well",
    ],
  },
];

const differentiators = [
  {
    title: "Built for messy reality",
    description:
      "I am not selling a fixed template. I look at the actual handoffs, exceptions, and workarounds your team deals with and design around them.",
  },
  {
    title: "Strong where native integrations stop",
    description:
      "A lot of businesses do not have a clean modern stack. I am comfortable bridging systems that were never designed to work together neatly.",
  },
  {
    title: "Engineer-grade reliability",
    description:
      "My background is in environments where mistakes are expensive. That shows up in how I test, roll out, document, and support operational automation.",
  },
  {
    title: "Measured against business impact",
    description:
      "The point is not to say something is automated. The point is fewer admin hours, cleaner data, faster response times, and less operational drag.",
  },
];

const goodFitSignals = [
  "Your team re-keys the same information into multiple systems",
  "Critical updates live in inboxes or spreadsheets instead of flowing automatically",
  "Projects or customer work start with a manual setup checklist",
  "Reporting is only accurate after someone spends hours cleaning data",
  "You have tried off-the-shelf integrations and they did not fit the real process",
];

const AutomationDetails = () => {
  return (
    <Jumbotron
      id="automation"
      className="m-0 text-white"
      style={{
        minHeight: "100vh",
        backgroundColor: "rgb(15, 18, 24)",
        backgroundImage:
          "radial-gradient(circle at 10% 12%, rgba(76, 201, 240, 0.14), transparent 28%), radial-gradient(circle at 90% 30%, rgba(244, 162, 97, 0.12), transparent 32%)",
      }}
    >
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
          <h1 className="display-5 mb-0">
            Custom Automation for Real Operational Work
          </h1>
          <Link
            to="/"
            className="btn btn-outline-light"
            style={{ borderRadius: "999px", padding: "0.5rem 1rem" }}
          >
            Back to Home
          </Link>
        </div>

        <p
          className="lead mb-5"
          style={{ maxWidth: "860px", color: "rgba(255, 255, 255, 0.84)" }}
        >
          TWSystems helps operations-heavy businesses remove repetitive admin,
          improve visibility, and connect systems that do not talk to each other
          cleanly. This is custom workflow automation for teams that need
          something more robust than a basic template or one-click integration.
        </p>

        <div className="row">
          {offerings.map((offering) => (
            <div className="col-lg-4 mb-4" key={offering.title}>
              <div
                className="h-100 rounded p-4"
                style={{
                  backgroundColor: "rgba(24, 28, 36, 0.92)",
                  border: "1px solid rgba(255, 255, 255, 0.16)",
                }}
              >
                <h2 className="h4 mb-3">{offering.title}</h2>
                <ul
                  className="mb-0 ps-3"
                  style={{
                    color: "rgba(255, 255, 255, 0.78)",
                    lineHeight: 1.7,
                  }}
                >
                  {offering.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-4 p-4 p-md-5 rounded"
          style={{
            backgroundColor: "rgba(24, 28, 36, 0.88)",
            border: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          <h3 className="h4 mb-3">What Makes This Different</h3>
          <div className="row">
            {differentiators.map((item) => (
              <div className="col-lg-6 mb-3" key={item.title}>
                <div
                  className="h-100 p-3 rounded"
                  style={{
                    backgroundColor: "rgba(15, 18, 24, 0.92)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  <h4 className="h6 mb-2">{item.title}</h4>
                  <p
                    className="mb-0"
                    style={{ color: "rgba(255, 255, 255, 0.78)" }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-4 p-4 rounded"
          style={{
            backgroundColor: "rgba(24, 28, 36, 0.84)",
            border: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          <h3 className="h5 mb-3">You are probably a good fit if...</h3>
          <ul
            className="mb-0 ps-3"
            style={{
              color: "rgba(255, 255, 255, 0.78)",
              lineHeight: 1.8,
            }}
          >
            {goodFitSignals.map((signal) => (
              <li key={signal}>{signal}</li>
            ))}
          </ul>
        </div>

        <div
          className="mt-4 p-4 rounded"
          style={{
            backgroundColor: "rgba(24, 28, 36, 0.84)",
            border: "1px solid rgba(255, 255, 255, 0.14)",
          }}
        >
          <h3 className="h5 mb-2">How projects typically run</h3>
          <p className="mb-0" style={{ color: "rgba(255, 255, 255, 0.78)" }}>
            Discovery and audit, workflow blueprint, build and integration,
            testing and rollout, then support and handover. You get regular
            updates, transparent delivery, and solutions shaped around how your
            team actually works rather than how a tool vendor assumes it should.
          </p>
        </div>
      </div>
    </Jumbotron>
  );
};

export default AutomationDetails;
